import { useCallback } from "react";

import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { normalizeComuna } from "~/utils/comunaNormalizer";
import { normalizeRegion } from "~/utils/regionNormalizer";

interface AddressImportData {
  address_id: string;
  street?: string;
  number?: string;
  city?: string;
  comuna?: string;
  region?: string;
}

// Normalize values for comparison - treats null, undefined, and empty string as equivalent
function normalizeValue(val: unknown): string {
  if (val === null || val === undefined || val === "") return "";
  return String(val).trim();
}

// Helper function to detect if address data has changed
function hasChanges(
  existing: Record<string, unknown>,
  newData: Record<string, unknown>
): boolean {
  return Object.keys(newData).some((key) => {
    const oldVal = normalizeValue(existing[key]);
    const newVal = normalizeValue(newData[key]);
    return oldVal !== newVal;
  });
}

export const useAddressImport = () => {
  const { toast } = useToast();

  const importAddresses = useCallback(
    async (addressesData: AddressImportData[]) => {
      try {
        // Get existing addresses for validation
        const addressIds = addressesData
          .map((a) => a.address_id)
          .filter(Boolean);

        if (addressIds.length === 0) {
          throw new Error("No se encontraron IDs de dirección válidos");
        }

        console.log(
          `Starting address import with ${addressesData.length} addresses`
        );

        const { data: existingAddresses, error: fetchError } = await supabase
          .from("addresses")
          .select("id, street, number, city, comuna, state")
          .in("id", addressIds);

        if (fetchError) throw fetchError;

        const existingById = new Map(
          existingAddresses?.map((addr) => [addr.id, addr]) || []
        );

        const updateRecords: Array<{ id: string; [key: string]: unknown }> = [];
        const skippedAddresses: string[] = [];
        const unchangedAddresses: string[] = [];

        for (const current of addressesData) {
          if (!current.address_id) {
            skippedAddresses.push("Sin ID");
            continue;
          }

          const existing = existingById.get(current.address_id);
          if (!existing) {
            skippedAddresses.push(current.address_id);
            continue;
          }

          const updateData: Record<string, unknown> = {};

          if (current.street !== undefined) updateData.street = current.street;
          if (current.number !== undefined) updateData.number = current.number;
          if (current.city !== undefined) updateData.city = current.city;
          if (current.comuna !== undefined)
            updateData.comuna = normalizeComuna(current.comuna);
          if (current.region !== undefined)
            updateData.state = normalizeRegion(current.region);

          if (
            Object.keys(updateData).length > 0 &&
            hasChanges(existing, updateData)
          ) {
            updateRecords.push({ id: current.address_id, ...updateData });
          } else {
            unchangedAddresses.push(current.address_id);
          }
        }

        console.log(
          `📊 Change detection: ${updateRecords.length} changed, ${unchangedAddresses.length} unchanged, ${skippedAddresses.length} not found`
        );

        let updatedCount = 0;
        const errors: string[] = [];
        const BATCH_SIZE = 10;

        // Process in batches
        if (updateRecords.length > 0) {
          const batches: Array<Array<{ id: string; [key: string]: unknown }>> =
            [];
          for (let i = 0; i < updateRecords.length; i += BATCH_SIZE) {
            batches.push(updateRecords.slice(i, i + BATCH_SIZE));
          }

          console.log(
            `📦 Processing ${batches.length} batches (${BATCH_SIZE} addresses each)...`
          );

          for (let i = 0; i < batches.length; i++) {
            const batch = batches[i];

            const updatePromises = batch.map((addr) => {
              const { id, ...updateData } = addr;
              return supabase.from("addresses").update(updateData).eq("id", id);
            });

            const results = await Promise.all(updatePromises);

            results.forEach((result, idx) => {
              if (result.error) {
                console.error(
                  `❌ Failed to update address ${batch[idx].id}:`,
                  result.error
                );
                errors.push(`Dirección ${batch[idx].id}: ${result.error.message}`);
              } else {
                updatedCount++;
              }
            });

            if ((i + 1) % 10 === 0 || i === batches.length - 1) {
              console.log(
                `✅ Progress: ${updatedCount}/${updateRecords.length} addresses updated`
              );
            }
          }
        }

        // Build feedback message
        let message = `${updatedCount} direcciones actualizadas`;
        if (unchangedAddresses.length > 0) {
          message += `, ${unchangedAddresses.length} sin cambios`;
        }
        if (skippedAddresses.length > 0) {
          message += `, ${skippedAddresses.length} no encontradas`;
        }

        if (skippedAddresses.length > 0) {
          console.error("🚨 DIRECCIONES NO ENCONTRADAS:");
          skippedAddresses.forEach((id) => console.error(`- ${id}`));
        }

        if (errors.length > 0) {
          console.error("🚨 ERRORES:");
          errors.forEach((error) => console.error(`- ${error}`));
        }

        let title = "Importación completada";
        if (updatedCount === 0 && unchangedAddresses.length > 0) {
          title = "Sin cambios detectados";
        } else if (skippedAddresses.length > 0) {
          title = "Importación completada con omisiones";
        }

        toast({
          title,
          description: message,
        });

        return { updatedCount, errors };
      } catch (err) {
        console.error("Error importing addresses:", err);
        toast({
          title: "Error al importar direcciones",
          description: "No se pudieron importar las direcciones.",
          variant: "destructive",
        });
        throw err;
      }
    },
    [toast]
  );

  return { importAddresses };
};
